import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Box, Paper, Typography } from '@mui/material';
import { AppRoutes } from 'AppRoutes';
import * as qs from 'query-string';

import {
  requestRequestResetPasswordAsyncAction,
  requestSetNewPasswordAsyncAction,
  selectPasswordRequestState,
  selectPasswordValidationErrors,
} from 'rdx/password';

import ForgotPasswordForm from 'components/ForgotPassword/ForgotPasswordForm';
import SetNewPasswordForm from 'components/ForgotPassword/SetNewPasswordForm';

import { useTitle } from '../../navigation/useTitle';

const queryStringObject = qs.parse(window.location.search);
const resetPasswordToken = queryStringObject?.token;

export const ForgotPassword = () => {
  useTitle('screens.ForgotPassword.title');
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();

  const [passwordChangeMessage, setPasswordChangeMessage] = useState<string | null>(null);
  const [requestPasswordMessage, setRequestPasswordMessage] = useState<string | null>(null);

  const requestState = useSelector(selectPasswordRequestState);
  const passwordRequestValidationErrors = useSelector(selectPasswordValidationErrors);

  const strings = useMemo(
    () => ({
      setNewPasswordForm: {
        passwordNotMatched: intl.formatMessage({
          id: 'screens.ForgotPassword.password_not_matched',
        }),
        passwordMinimumRequirements: intl.formatMessage({
          id: 'screens.ForgotPassword.password_minimum_requirements',
        }),
        passwordChanged: intl.formatMessage({ id: 'screens.ForgotPassword.password_changed' }),
        error: intl.formatMessage({ id: 'screens.ForgotPassword.failure' }),
        password: intl.formatMessage({ id: 'screens.ForgotPassword.password' }),
        confirmPassword: intl.formatMessage({ id: 'screens.ForgotPassword.confirm_password' }),
        setPassword: intl.formatMessage({ id: 'screens.ForgotPassword.set_password' }),
        goToLogin: intl.formatMessage({ id: 'screens.ForgotPassword.got_to_login' }),
      },
      forgotPasswordForm: {
        error: intl.formatMessage({ id: 'screens.ForgotPassword.failure' }),
        emailSend: intl.formatMessage({ id: 'screens.ForgotPassword.email_send' }),
        username: intl.formatMessage({ id: 'screens.ForgotPassword.username' }),
        resetPassword: intl.formatMessage({ id: 'screens.ForgotPassword.reset_password' }),
      },
    }),
    [intl],
  );

  const onNewPasswordSubmitHandler = useCallback(
    (payload) => {
      dispatch(requestSetNewPasswordAsyncAction.request(payload));
    },
    [dispatch],
  );

  const onResetPasswordHandler = useCallback(
    (username) => {
      dispatch(requestRequestResetPasswordAsyncAction.request(username));
    },
    [dispatch],
  );

  useEffect(() => {
    if (passwordRequestValidationErrors && passwordRequestValidationErrors.password) {
      setPasswordChangeMessage(passwordRequestValidationErrors.password.join('<br/>'));
      return;
    }

    if (passwordRequestValidationErrors && passwordRequestValidationErrors.email) {
      setRequestPasswordMessage(passwordRequestValidationErrors.email.join('<br/>'));
      return;
    }

    setRequestPasswordMessage(null);
    setPasswordChangeMessage(null);
  }, [passwordRequestValidationErrors]);

  const onGoBackToLoginHandler = useCallback(() => {
    history.push(AppRoutes.AUTH);
  }, [history]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
    >
      <Box sx={{ maxWidth: '450px', width: '85vw' }}>
        <Paper>
          <Box py={4} px={3}>
            <Typography variant="h6">
              {!resetPasswordToken
                ? intl.formatMessage({ id: 'screens.ForgotPassword.title' })
                : intl.formatMessage({ id: 'screens.ForgotPassword.set_password_title' })}
            </Typography>
            {!resetPasswordToken ? (
              <Typography variant="bodyM">
                {intl.formatMessage({ id: 'screens.ForgotPassword.description_password_reset' })}
              </Typography>
            ) : null}
            <Box>
              {!resetPasswordToken ? (
                <ForgotPasswordForm
                  strings={strings.forgotPasswordForm}
                  onResetPassword={onResetPasswordHandler}
                  alertMessage={requestPasswordMessage}
                  requestState={requestState}
                />
              ) : (
                <SetNewPasswordForm
                  token={resetPasswordToken.toString()}
                  strings={strings.setNewPasswordForm}
                  onSubmit={onNewPasswordSubmitHandler}
                  onGoBackToLogin={onGoBackToLoginHandler}
                  alertMessage={passwordChangeMessage}
                  requestState={requestState}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
